import { z } from 'zod'
import {
  AGAMA_UMAT,
  ASURANSI_KESEHATAN,
  GOLONGAN_DARAH,
  HUBUNGAN,
  JENIS_KELAMIN,
  PENDAPATAN_BULANAN,
  PENDIDIKAN_SAAT_INI,
  STATUS_GEREJA,
  STATUS_HIDUP,
  STATUS_PERKAWINAN,
} from '../schema/contract'
import {
  anakKeSchema,
  emailSchema,
  keterampilanSchema,
  nikSchema,
  optionalTextSchema,
  partialDateSchema,
  phoneSchema,
  referensiSakramenLuarSchema,
  requiredTextSchema,
  syncShape,
  tahunLulusSchema,
  uuidSchema,
} from './kolom'

/** `umat` domain columns (spec 8.3). */
export const umatSchema = z.object({
  lingkungan_id: uuidSchema.nullish(),
  nama_lengkap: requiredTextSchema,
  nama_baptis: optionalTextSchema,
  nama_panggilan: optionalTextSchema,
  jenis_kelamin: z.enum(JENIS_KELAMIN).nullish(),
  tempat_lahir: optionalTextSchema,
  tanggal_lahir: partialDateSchema.nullish(),
  nik: nikSchema.nullish(),
  nomor_akta_lahir: optionalTextSchema,
  status_gereja: z.enum(STATUS_GEREJA).default('katolik'),
  /** The parish data sheet's fields (schema 15), each optional. */
  agama: z.enum(AGAMA_UMAT).nullish(),
  agama_sebelumnya: z.enum(AGAMA_UMAT).nullish(),
  gereja_asal: optionalTextSchema,
  anak_ke: anakKeSchema.nullish(),
  status_perkawinan: z.enum(STATUS_PERKAWINAN).nullish(),
  pendidikan_kode: optionalTextSchema,
  pendidikan_saat_ini: z.enum(PENDIDIKAN_SAAT_INI).nullish(),
  jurusan: optionalTextSchema,
  nama_sekolah: optionalTextSchema,
  tahun_lulus: tahunLulusSchema.nullish(),
  pekerjaan_kode: optionalTextSchema,
  profesi: optionalTextSchema,
  anggota_cu: z.boolean().nullish(),
  keterampilan: keterampilanSchema.nullish(),
  telepon: phoneSchema.nullish(),
  email: emailSchema.nullish(),
  keluarga_id: uuidSchema.nullish(),
  hubungan: z.enum(HUBUNGAN).nullish(),
  ayah_id: uuidSchema.nullish(),
  ibu_id: uuidSchema.nullish(),
  nama_ayah: optionalTextSchema,
  nama_ibu: optionalTextSchema,
  status_hidup: z.enum(STATUS_HIDUP).default('hidup'),
  tanggal_meninggal: partialDateSchema.nullish(),
  /** Pindah ke keuskupan lain (schema 13, ../pindahKeuskupan). */
  pindah_keuskupan: optionalTextSchema,
  pindah_paroki: optionalTextSchema,
  pindah_tanggal: partialDateSchema.nullish(),
  baptis_id: uuidSchema.nullish(),
  komuni_id: uuidSchema.nullish(),
  krisma_id: uuidSchema.nullish(),
  referensi_sakramen_luar: referensiSakramenLuarSchema.nullish(),
  /** The person's own address (upacara bersama, Part B): only while they have no family. */
  alamat: optionalTextSchema,
  rt_rw: optionalTextSchema,
  kampung_desa: optionalTextSchema,
  kecamatan: optionalTextSchema,
  kabupaten_kota: optionalTextSchema,
  provinsi: optionalTextSchema,
  catatan: optionalTextSchema,
  /** Hapus umat (schema 17, ../hapusData): the reason, as typed. */
  alasan_hapus: optionalTextSchema,
})
export type Umat = z.infer<typeof umatSchema>
export const umatRowSchema = umatSchema.extend(syncShape)
export type UmatRow = z.infer<typeof umatRowSchema>
export const umatFieldsSchema = z.strictObject(umatRowSchema.omit({ _hlc: true }).partial().shape)

/**
 * `umat_profil` (schema 15): a person's sensitive fields, staff and admins only. The row's `id` is
 * the person's; each field optional.
 */
export const umatProfilSchema = z.object({
  golongan_darah: z.enum(GOLONGAN_DARAH).nullish(),
  suku: optionalTextSchema,
  pendapatan_bulanan: z.enum(PENDAPATAN_BULANAN).nullish(),
  asuransi_kesehatan: z.enum(ASURANSI_KESEHATAN).nullish(),
})
export type UmatProfil = z.infer<typeof umatProfilSchema>
export const umatProfilRowSchema = umatProfilSchema.extend(syncShape)
export type UmatProfilRow = z.infer<typeof umatProfilRowSchema>
export const umatProfilFieldsSchema = z.strictObject(
  umatProfilRowSchema.omit({ _hlc: true }).partial().shape,
)
